import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, X, CheckCircle2, AlertTriangle, MinusCircle, FileSpreadsheet, Info } from "lucide-react";
import { api, API, getStoredToken, formatApiError, RESOURCE_CATEGORIES } from "@/lib/api";

/**
 * Bulk-import dialog for the Resources directory.
 * - Lets the user download a CSV/XLSX template.
 * - Drops or picks a file -> POSTs to /resources/import?commit=false for preview.
 * - Shows a per-row classification table (ok / skip_duplicate / error).
 * - "Confirm import" re-uploads with commit=true; refreshes parent on close.
 */
export default function ResourceImportDialog({ open, onClose, onImported }) {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {rows, summary, ...}
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [dragging, setDragging] = useState(false);

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setPreview(null);
    setBusy(false);
    setCommitting(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const close = () => { reset(); onClose?.(); };

  const downloadTemplate = async (kind) => {
    try {
      const url = `${API}/resources/import-template.${kind}`;
      const token = getStoredToken();
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `resources_import_template.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runPreview = async (f) => {
    setBusy(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("commit", "false");
      const { data } = await api.post("/resources/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handlePick = (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx") && !name.endsWith(".txt")) {
      toast.error("Please upload a .csv or .xlsx file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setFile(f);
    runPreview(f);
  };

  const commit = async () => {
    if (!file || !preview) return;
    if (!preview.summary?.ok) {
      toast.message("Nothing to import — all rows are duplicates or errors.");
      return;
    }
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit", "true");
      const { data } = await api.post("/resources/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.committed} resource${data.committed === 1 ? "" : "s"}`);
      onImported?.(data);
      close();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCommitting(false);
    }
  };

  const summary = preview?.summary;
  const STATUS_META = {
    ok:             { Icon: CheckCircle2, color: "text-[#166534]", bg: "bg-[#E8ECE7]", label: "Will import" },
    skip_duplicate: { Icon: MinusCircle,  color: "text-[#92400E]", bg: "bg-[#FEF3C7]", label: "Skip · duplicate" },
    error:          { Icon: AlertTriangle, color: "text-[#991B1B]", bg: "bg-[#FEE2E2]", label: "Error" },
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="resource-import-dialog">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A]">Bulk import resources</h2>
            <p className="text-xs text-[#334155] mt-1">
              Upload a CSV or Excel file. We'll preview every row before anything is saved.
            </p>
          </div>
          <button onClick={close} className="p-2 hover:bg-[#F9F8F6] rounded-full" data-testid="resource-import-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Templates */}
          <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="w-5 h-5 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-sm text-[#0F172A]">Need a starting point?</div>
                <p className="text-xs text-[#334155] mt-1">
                  Download a template with the exact columns and an example row. Required: <b>name</b>, <b>category</b>.
                  List fields (tags, languages_spoken, service_area_counties) use <code>;</code> or <code>,</code> separators.
                </p>
                <div className="mt-3 rounded-lg bg-white border border-[#E5E5E0] p-3" data-testid="resource-import-allowed-categories">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#1E3A8A]">
                    <Info className="w-3.5 h-3.5" /> Accepted category values
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {RESOURCE_CATEGORIES.map((c) => (
                      <span
                        key={c.value}
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#F1F5F9] border border-[#E5E5E0] text-[11px] text-[#0F172A]"
                        title={`Type "${c.value}" or "${c.label}"`}
                      >
                        <span className="font-mono text-[10px] text-[#1E3A8A] mr-1">{c.value}</span>
                        <span className="text-[#334155]">· {c.label}</span>
                      </span>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-[#334155]">
                    Either the slug or the human label is accepted (case-insensitive). Unknown values are flagged in the preview before commit.
                  </p>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => downloadTemplate("csv")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs font-medium text-[#1E3A8A] hover:bg-[#F9F8F6]"
                    data-testid="resource-import-template-csv"
                  >
                    <Download className="w-3.5 h-3.5" /> CSV template
                  </button>
                  <button
                    onClick={() => downloadTemplate("xlsx")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs font-medium text-[#1E3A8A] hover:bg-[#F9F8F6]"
                    data-testid="resource-import-template-xlsx"
                  >
                    <Download className="w-3.5 h-3.5" /> Excel template
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer?.files?.[0];
              if (f) handlePick(f);
            }}
            onClick={() => fileInput.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
              dragging ? "border-[#1E3A8A] bg-[#F1F5F9]" : "border-[#E5E5E0] hover:bg-[#F9F8F6]"
            }`}
            data-testid="resource-import-dropzone"
          >
            <Upload className="w-8 h-8 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-sm font-medium text-[#0F172A]">
              {file ? file.name : "Drop your file here or click to browse"}
            </div>
            <div className="text-xs text-[#334155] mt-1">CSV or XLSX · 5 MB max</div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handlePick(e.target.files?.[0])}
              data-testid="resource-import-file-input"
            />
          </div>

          {/* Preview summary */}
          {busy && (
            <div className="text-center text-sm text-[#1E3A8A] py-6">Parsing file…</div>
          )}

          {preview && summary && (
            <>
              {summary.error > 0 && (
                <div
                  className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-3"
                  data-testid="resource-import-error-banner"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#991B1B] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#991B1B]">
                        {summary.error} row{summary.error === 1 ? "" : "s"} need attention before import
                      </div>
                      <p className="text-xs text-[#7F1D1D] mt-1">
                        Fix the rows below, then re-upload. Accepted category values:
                      </p>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {RESOURCE_CATEGORIES.map((c) => (
                          <span key={c.value} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-[#FCA5A5] text-[11px] text-[#991B1B]">
                            <span className="font-mono text-[10px] mr-1">{c.value}</span>
                            <span>· {c.label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="resource-import-summary">
                <SummaryCard label="Total rows" value={summary.total} />
                <SummaryCard label="Will import" value={summary.ok} accent="text-[#166534]" />
                <SummaryCard label="Skip · duplicate" value={summary.skip_duplicate} accent="text-[#92400E]" />
                <SummaryCard label="Errors" value={summary.error} accent="text-[#991B1B]" />
              </div>

              <div className="bg-white border border-[#E5E5E0] rounded-xl overflow-hidden">
                <table className="w-full text-sm" data-testid="resource-import-preview-table">
                  <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider w-16">Row</th>
                      <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Status</th>
                      <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Resource</th>
                      <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden md:table-cell">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => {
                      const meta = STATUS_META[r.status] || STATUS_META.error;
                      const Icon = meta.Icon;
                      return (
                        <tr key={r.row_number} className="border-t border-[#F0EFEB]" data-testid={`resource-import-row-${r.row_number}`}>
                          <td className="px-3 py-2 text-xs text-[#334155]">{r.row_number}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider ${meta.bg} ${meta.color}`}>
                              <Icon className="w-3 h-3" /> {meta.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-[#0F172A]">
                            <div className="font-medium">{r.name || <span className="text-[#0F172A]/40">(missing)</span>}</div>
                            {r.county && <div className="text-xs text-[#334155]">{r.county}</div>}
                          </td>
                          <td className="px-3 py-2 text-xs text-[#334155] hidden md:table-cell">{r.message}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E5E0] flex items-center justify-between gap-3 flex-wrap bg-[#F9F8F6]">
          <button
            onClick={close}
            className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm font-medium text-[#334155] hover:bg-white"
            data-testid="resource-import-cancel"
          >
            Cancel
          </button>
          <button
            onClick={commit}
            disabled={!preview || !summary?.ok || committing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="resource-import-confirm"
          >
            <Upload className="w-4 h-4" />
            {committing
              ? "Importing…"
              : summary?.ok
                ? `Import ${summary.ok} resource${summary.ok === 1 ? "" : "s"}`
                : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent = "text-[#0F172A]" }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A] font-medium">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${accent}`}>{value ?? 0}</div>
    </div>
  );
}
